/** @jsxImportSource @emotion/react */
import { ComponentType, FC, ReactNode } from 'react';
import { breakpoints } from '@zip/business-components';
import { Box, Grid, useMediaQuery } from '@mui/material';
import { theme } from 'global';
import { useMerchantApplicationContext } from 'contexts/MerchantApplication.context';
import { ProgressBar } from './ProgressBar';
import { NavButtons } from '../nav-buttons/NavButtons';
import {
  signUpStepsConfig,
  getStepTitle,
  getStepDescription,
  SignUpStepKey,
} from 'modules/sign-up/signUpConfig';
import { MarketingSection } from 'modules/sign-up/MarketingSection';
import { StepComponentNavProps } from 'types/interfaces/sign-up/MerchantApplication.types';
import * as styles from 'modules/sign-up/SignUpFlow.styles';

export interface WithSignUpCommonLayoutOptions {
  leftPanel?: ReactNode;
  showTitle?: boolean;
  showProgressBar?: boolean;
  showNavButtons?: boolean;
}

export function withSignUpCommonLayout<P extends Record<string, any>>(
  WrappedComponent: ComponentType<P>,
  options?: WithSignUpCommonLayoutOptions
): FC<P & StepComponentNavProps> {
  const {
    leftPanel,
    showTitle = true,
    showProgressBar = true,
    showNavButtons = true,
  } = options ?? {};

  const hasLeftPanel = !!leftPanel;

  const WithSignUpCommonLayout: FC<P & StepComponentNavProps> = (props) => {
    const { stepState, getStepField } = useMerchantApplicationContext();
    const isDesktop = useMediaQuery(breakpoints.up(theme.breaks.mdOnboarding));
    const rawTitle = getStepTitle(stepState.activeStepKey);
    const stepDescription = getStepDescription(stepState.activeStepKey);
    const firstName = getStepField(SignUpStepKey.SignUpInfo, 'firstName') as
      | string
      | undefined;
    const stepTitle = rawTitle?.replace(
      '[First Name]',
      firstName?.trim() || ''
    );

    const {
      canGoBack,
      canContinue,
      isSubmitting,
      onBack,
      onContinue,
      ...wrappedProps
    } = props as P & StepComponentNavProps;

    return (
      <Box css={hasLeftPanel ? styles.pageWrapper : styles.pageWrapperPlain}>
        <Grid container columns={12} css={styles.gridContainer}>
          {hasLeftPanel && isDesktop && (
            <>
              <Grid
                size={{ xxs: 12, mdOnboarding: 5 }}
                css={styles.leftPanelGrid}
              >
                {leftPanel}
              </Grid>

              <Grid size={{ mdOnboarding: 2 }} />
            </>
          )}
          <Grid
            size={{
              xxs: 12,
              mdOnboarding: hasLeftPanel && isDesktop ? 5 : 12,
            }}
            css={styles.rightPanelGrid(hasLeftPanel)}
          >
            <Box
              css={styles.rightPanelInnerContent}
              data-id="rightPanelInnerContent"
            >
              {showTitle && stepTitle && <h1>{stepTitle}</h1>}
              {stepDescription && (
                <p className="body4" css={styles.compactStepDescription}>
                  {stepDescription}
                </p>
              )}

              {showProgressBar && (
                <ProgressBar
                  steps={signUpStepsConfig}
                  activeStepKey={stepState.activeStepKey}
                />
              )}

              <WrappedComponent
                {...(wrappedProps as any)}
                canGoBack={canGoBack}
                canContinue={canContinue}
                isSubmitting={isSubmitting}
                onBack={onBack}
                onContinue={onContinue}
              />
              {showNavButtons && onContinue && (
                <NavButtons
                  canGoBack={canGoBack ?? false}
                  canContinue={canContinue ?? true}
                  isSubmitting={isSubmitting ?? false}
                  onBack={onBack ?? (() => {})}
                  onContinue={onContinue}
                />
              )}
            </Box>
          </Grid>
        </Grid>
      </Box>
    );
  };

  const wrappedName =
    WrappedComponent.displayName || WrappedComponent.name || 'Component';
  WithSignUpCommonLayout.displayName = `withSignUpCommonLayout(${wrappedName})`;

  return WithSignUpCommonLayout;
}

export function withSignUpCommonLayoutAndMarketingSection<
  P extends Record<string, any>,
>(WrappedComponent: ComponentType<P>): FC<P & StepComponentNavProps> {
  return withSignUpCommonLayout(WrappedComponent, {
    leftPanel: <MarketingSection />,
  });
}
